/**
 * =====================================================================================
 * MÓDULO VUEX DE AUTENTICACIÓN - auth.js
 * =====================================================================================
 *
 * Este módulo maneja todo lo relacionado con la autenticación de usuarios:
 * - Login (inicio de sesión)
 * - Logout (cierre de sesión)
 * - Persistencia de sesión (localStorage)
 * - Datos del usuario actual
 * - Ambiente de trabajo (Pruebas vs Producción)
 *
 * ¿QUÉ ES UN MÓDULO VUEX?
 * -----------------------
 * Un módulo es una porción del store que maneja un área específica.
 * Tiene su propio state, getters, mutations y actions.
 *
 * NAMESPACED:
 * -----------
 * Este módulo tiene `namespaced: true` lo que significa:
 * - Sus actions se llaman con: store.dispatch('auth/login', ...)
 * - Sus getters se acceden con: store.getters['auth/isAuthenticated']
 * - Evita conflictos de nombres con otros módulos
 *
 * FLUJO DE AUTENTICACIÓN:
 * -----------------------
 *
 * LOGIN:
 * ┌─────────────────┐
 * │ LoginView.vue   │ Usuario ingresa credenciales
 * └────────┬────────┘
 *          │ dispatch('auth/login', credentials)
 *          ▼
 * ┌─────────────────┐
 * │ ACTION: login   │ Llama authService.login()
 * └────────┬────────┘
 *          │
 *          ▼ Backend responde con { token, user }
 * ┌─────────────────┐
 * │ MUTATIONS       │ SET_USER, SET_TOKEN, SET_AMBIENTE
 * └────────┬────────┘
 *          │
 *          ▼ Guardar en localStorage
 * ┌─────────────────┐
 * │ localStorage    │ dte_token, dte_user, dte_ambiente
 * └─────────────────┘
 *
 * RESTAURAR SESIÓN (al recargar la página):
 * ┌─────────────────┐
 * │ App.vue         │ Al montar la app
 * └────────┬────────┘
 *          │ dispatch('auth/restoreSession')
 *          ▼
 * ┌─────────────────┐
 * │ localStorage    │ Lee token y user guardados
 * └────────┬────────┘
 *          │ Si existen y son válidos
 *          ▼
 * ┌─────────────────┐
 * │ MUTATIONS       │ SET_USER, SET_TOKEN (restaura state)
 * └─────────────────┘
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - src/services/auth.service.js: Hace las peticiones HTTP de login
 * - src/views/LoginView.vue: Formulario de login que dispara actions
 * - src/router/index.js: Guard de navegación que verifica isAuthenticated
 * - src/services/api.js: Interceptor que usa el token para autorización
 *
 * =====================================================================================
 */

// Importamos el servicio que hace las peticiones HTTP de autenticación
import authService from '@/services/auth.service'

/**
 * =====================================================================================
 * STATE (Estado del Módulo)
 * =====================================================================================
 *
 * El state contiene los datos que este módulo gestiona.
 * Es reactivo - cuando cambia, los componentes que lo usan se actualizan.
 *
 * ESTRUCTURA DEL USER CUANDO HAY SESIÓN:
 * {
 *   id: 1,                    // ID del usuario en la BD
 *   email: "user@email.com",  // Email del usuario
 *   nombre: "Juan Pérez",     // Nombre completo
 *   emisorId: 1,              // ID del emisor asociado
 *   emisorNombre: "Mi Empresa", // Nombre del emisor
 *   rol: "Administrador",     // Nombre del rol
 *   rolId: 1,                 // ID del rol
 *   sucursalId: 1,            // ID de sucursal asignada (null para SuperAdmin/EmisorAdmin/Auditor)
 *   sucursalNombre: "Casa Matriz", // Nombre de sucursal asignada
 *   permisos: ["facturas.crear", "facturas.ver", ...]
 * }
 */
const state = {
  // Datos del usuario autenticado (null si no hay sesión)
  user: null,

  // Flag que indica si el usuario está autenticado
  isAuthenticated: false,

  // Ambiente de trabajo: '00'=Pruebas, '01'=Producción
  // Determina a qué servidor del MH se envían los documentos
  ambiente: '00',

  // Token JWT de autenticación (se envía en cada petición HTTP)
  token: null,

  // Contraseña temporal guardada durante login para cambio obligatorio
  // Se limpia después del cambio de contraseña exitoso
  tempPasswordForChange: null
}

/**
 * =====================================================================================
 * GETTERS (Propiedades Computadas del Estado)
 * =====================================================================================
 *
 * Los getters son como computed properties pero para el store.
 * Se cachean y solo se recalculan cuando cambia el state que usan.
 *
 * USO EN COMPONENTES:
 * -------------------
 * // Composition API
 * const isAuth = computed(() => store.getters['auth/isAuthenticated'])
 *
 * // Options API con mapGetters
 * computed: {
 *   ...mapGetters('auth', ['isAuthenticated', 'currentUser'])
 * }
 */
const getters = {
  /**
   * Retorna los datos del usuario actual
   * @returns {Object|null} Datos del usuario o null si no hay sesión
   */
  currentUser: state => state.user,

  /**
   * Indica si hay un usuario autenticado
   * @returns {boolean} true si hay sesión activa
   */
  isAuthenticated: state => state.isAuthenticated,

  /**
   * Retorna el código del ambiente actual
   * @returns {string} '00' para pruebas, '01' para producción
   */
  ambiente: state => state.ambiente,

  /**
   * Retorna descripción legible del ambiente
   * Útil para mostrar en la UI: "Ambiente: Pruebas"
   *
   * OPERADOR TERNARIO:
   * condición ? valorSiTrue : valorSiFalse
   *
   * @returns {string} 'Pruebas' o 'Productivo'
   */
  ambienteDescripcion: state => state.ambiente === '00' ? 'Pruebas' : 'Productivo',

  /**
   * Retorna el nombre del rol del usuario
   * Usa optional chaining (?.) para evitar error si user es null
   * @returns {string|null} Nombre del rol o null
   */
  userRole: state => state.user?.rol || null,

  /**
   * Retorna el nombre del usuario para mostrar
   * Con fallback a 'Usuario' si no hay nombre
   * @returns {string} Nombre del usuario
   */
  userName: state => state.user?.nombre || 'Usuario',

  /**
   * Retorna el ID de la sucursal asignada al usuario
   * NULL para roles sin restricción (SuperAdmin, EmisorAdmin, Auditor)
   * @returns {number|null} ID de sucursal o null
   */
  userSucursalId: state => state.user?.sucursalId || null,

  /**
   * Retorna el nombre de la sucursal asignada
   * @returns {string|null} Nombre de sucursal o null
   */
  userSucursalNombre: state => state.user?.sucursalNombre || null,

  /**
   * Indica si el usuario tiene restricción de sucursal
   * Los roles GerenteSucursal, Cajero y Contador tienen restricción
   * @returns {boolean} true si tiene sucursal fija asignada
   */
  tieneSucursalFija: state => state.user?.sucursalId != null,

  /**
   * Indica si el usuario debe cambiar su contraseña
   * Se usa para forzar cambio de contraseña en primer login o después de reset
   * @returns {boolean} true si debe cambiar contraseña
   */
  requiereCambioPwd: state => state.user?.requiereCambioPwd || false,

  /**
   * Retorna la contraseña temporal guardada durante el login
   * Se usa para auto-llenar el campo de contraseña actual en el modal de cambio obligatorio
   * @returns {string|null} Contraseña temporal o null
   */
  tempPasswordForChange: state => state.tempPasswordForChange
}

/**
 * =====================================================================================
 * MUTATIONS (Modificadores del Estado)
 * =====================================================================================
 *
 * Las mutations son las ÚNICAS funciones que pueden modificar el state directamente.
 * Deben ser SÍNCRONAS (no pueden tener await).
 *
 * CONVENCIÓN DE NOMBRES:
 * - Se escriben en MAYÚSCULAS_CON_GUIONES
 * - Describen qué hacen: SET_USER, CLEAR_AUTH
 * - Reciben (state, payload) como parámetros
 *
 * USO:
 * - Desde actions: commit('SET_USER', userData)
 * - Desde componentes (raro): store.commit('auth/SET_USER', userData)
 */
const mutations = {
  /**
   * Establece los datos del usuario
   *
   * OPERADOR !!:
   * - !!null → false
   * - !!{} → true
   * Doble negación convierte cualquier valor a booleano
   *
   * @param {Object} state - Estado del módulo (inyectado por Vuex)
   * @param {Object|null} user - Datos del usuario o null para limpiar
   */
  SET_USER(state, user) {
    state.user = user
    // Si hay user, isAuthenticated es true; si user es null, es false
    state.isAuthenticated = !!user
  },

  /**
   * Establece el ambiente de trabajo
   * @param {Object} state - Estado del módulo
   * @param {string} ambiente - '00' para pruebas o '01' para producción
   */
  SET_AMBIENTE(state, ambiente) {
    state.ambiente = ambiente
  },

  /**
   * Establece el token JWT
   * @param {Object} state - Estado del módulo
   * @param {string|null} token - Token JWT o null
   */
  SET_TOKEN(state, token) {
    state.token = token
  },

  /**
   * Limpia toda la información de autenticación
   * Se usa en logout para resetear el estado
   * @param {Object} state - Estado del módulo
   */
  CLEAR_AUTH(state) {
    state.user = null
    state.isAuthenticated = false
    state.token = null
    // NOTA: NO limpiamos ambiente porque podría querer mantenerlo
  },

  /**
   * Limpia el flag de requerir cambio de contraseña
   * Se llama después de que el usuario cambia su contraseña exitosamente
   * @param {Object} state - Estado del módulo
   */
  CLEAR_REQUIERE_CAMBIO_PWD(state) {
    if (state.user) {
      state.user.requiereCambioPwd = false
      // Actualizar localStorage
      localStorage.setItem('dte_user', JSON.stringify(state.user))
    }
    // También limpiar la contraseña temporal guardada
    state.tempPasswordForChange = null
  },

  /**
   * Guarda temporalmente la contraseña usada en login
   * Se usa para auto-llenar el campo en el modal de cambio obligatorio
   * @param {Object} state - Estado del módulo
   * @param {string} password - Contraseña a guardar
   */
  SET_TEMP_PASSWORD(state, password) {
    state.tempPasswordForChange = password
  },

  /**
   * Limpia la contraseña temporal guardada
   * @param {Object} state - Estado del módulo
   */
  CLEAR_TEMP_PASSWORD(state) {
    state.tempPasswordForChange = null
  }
}

/**
 * =====================================================================================
 * ACTIONS (Acciones Asíncronas)
 * =====================================================================================
 *
 * Las actions pueden contener lógica asíncrona (await, llamadas a APIs).
 * Cuando necesitan modificar el state, llaman a mutations con commit().
 *
 * CONTEXTO DE LAS ACTIONS:
 * El primer parámetro es un objeto con:
 * - commit: función para llamar mutations
 * - dispatch: función para llamar otras actions
 * - state: el estado actual del módulo
 * - rootState: el estado de toda la app
 * - getters: los getters del módulo
 * - rootGetters: getters de toda la app
 *
 * USO EN COMPONENTES:
 * -------------------
 * await store.dispatch('auth/login', { usuario, password, ambiente })
 */
const actions = {
  /**
   * ===========================================================================
   * ACTION: login - Autenticación con Backend Real
   * ===========================================================================
   *
   * Autentica un usuario con el backend.
   *
   * PROCESO:
   * 1. Llamar al servicio de autenticación con las credenciales
   * 2. Si es exitoso, extraer datos del usuario de la respuesta
   * 3. Guardar en el state (mutations)
   * 4. Guardar en localStorage para persistencia
   * 5. Retornar resultado
   *
   * @param {Object} context - Contexto de Vuex (desestructuramos commit)
   * @param {Object} credentials - { usuario, password, ambiente }
   * @returns {Object} { success: boolean, user?: Object, message?: string }
   */
  async login({ commit }, { usuario, password, ambiente }) {
    try {
      // PASO 1: Llamar al backend para autenticación
      // authService.login hace POST /api/Auth/login
      const response = await authService.login(usuario, password, ambiente)

      // PASO 2: El backend devuelve estructura diferente, la transformamos
      // Estructura backend: { token, userId, email, nombreCompleto, emisorId, emisorNombre, rolNombre, sucursalIds, permisos, ... }
      // Estructura que usamos en el frontend:
      const sucursalIds = response.sucursalIds || []
      const userData = {
        id: response.userId,
        email: response.email,
        nombre: response.nombreCompleto,
        emisorId: response.emisorId,
        emisorNombre: response.emisorNombre,
        rol: response.rolNombre,
        rolId: response.rolId,
        // Sucursales asignadas (array)
        sucursalIds,
        // Sucursal principal (derivada: primera del array si solo tiene una)
        sucursalId: response.sucursalId || (sucursalIds.length === 1 ? sucursalIds[0] : null),
        sucursalNombre: response.sucursalNombre || null,
        permisos: response.permisos || [],
        // Flag para forzar cambio de contraseña en primer login
        requiereCambioPwd: response.requiereCambioPwd || false
      }

      // PASO 3: Actualizar el state llamando mutations
      commit('SET_USER', userData)
      commit('SET_AMBIENTE', ambiente)
      commit('SET_TOKEN', response.token)

      // Si requiere cambio de contraseña, guardar la contraseña temporalmente
      // para auto-llenar el campo en el modal de cambio obligatorio
      if (userData.requiereCambioPwd) {
        commit('SET_TEMP_PASSWORD', password)
      }

      // PASO 4: Guardar en localStorage para persistencia
      // localStorage persiste incluso si el usuario cierra el navegador
      localStorage.setItem('dte_user', JSON.stringify(userData))
      localStorage.setItem('dte_ambiente', ambiente)
      localStorage.setItem('dte_token', response.token)

      // PASO 5: Retornar éxito con los datos del usuario
      return { success: true, user: userData }
    } catch (error) {
      // Si el login falla, loguear el error para debugging
      console.error('Error en login:', error)

      // Extraer mensaje de error del backend, o usar mensaje genérico
      // error.response?.data?.message usa optional chaining para evitar errores
      const message = error.response?.data?.message || 'Credenciales incorrectas. Verifique su email y contraseña.'
      return { success: false, message }
    }
  },

  /**
   * ===========================================================================
   * ACTION: logout - Cierre de sesión
   * ===========================================================================
   *
   * Cierra la sesión del usuario.
   *
   * PROCESO:
   * 1. Limpiar el state (mutation CLEAR_AUTH)
   * 2. Limpiar localStorage
   *
   * NOTA: Esta action es síncrona pero seguimos el patrón de action
   * para consistencia y posible futura llamada al backend.
   *
   * @param {Object} context - Contexto de Vuex
   */
  logout({ commit }) {
    // Limpiar state
    commit('CLEAR_AUTH')

    // Limpiar localStorage
    // removeItem no lanza error si la key no existe
    localStorage.removeItem('dte_user')
    localStorage.removeItem('dte_ambiente')
    localStorage.removeItem('dte_token')
  },

  /**
   * ===========================================================================
   * ACTION: restoreSession - Restaurar sesión desde localStorage
   * ===========================================================================
   *
   * Restaura la sesión desde localStorage.
   * Se llama al iniciar la aplicación para verificar si hay sesión previa.
   *
   * ¿POR QUÉ ES NECESARIO?
   * ----------------------
   * Cuando el usuario recarga la página (F5), el state de Vuex se pierde
   * porque está en memoria. Pero localStorage persiste, así que podemos
   * recuperar la sesión de ahí.
   *
   * PROCESO:
   * 1. Leer datos de localStorage
   * 2. Si existen y son válidos, restaurar el state
   * 3. Retornar si había sesión o no
   *
   * @param {Object} context - Contexto de Vuex
   * @returns {boolean} true si se restauró sesión, false si no había
   */
  restoreSession({ commit }) {
    // Leer datos guardados
    const savedUser = localStorage.getItem('dte_user')
    const savedAmbiente = localStorage.getItem('dte_ambiente')
    const savedToken = localStorage.getItem('dte_token')

    // Verificar que existan user Y token (ambos son necesarios)
    if (savedUser && savedToken) {
      try {
        // JSON.parse convierte el string a objeto JavaScript
        // Puede fallar si el string es inválido, por eso try/catch
        const user = JSON.parse(savedUser)

        // Verificar que el token no esté expirado (decodificación básica de JWT)
        try {
          const tokenPayload = JSON.parse(atob(savedToken.split('.')[1]))
          const expiration = tokenPayload.exp * 1000 // Convertir a milisegundos
          if (Date.now() >= expiration) {
            // Token expirado, limpiar localStorage
            console.warn('Token expirado, limpiando sesión')
            localStorage.removeItem('dte_user')
            localStorage.removeItem('dte_token')
            localStorage.removeItem('dte_ambiente')
            return false
          }
        } catch (tokenError) {
          // No se pudo decodificar el token, asumir que es inválido
          console.warn('Token inválido, limpiando sesión')
          localStorage.removeItem('dte_user')
          localStorage.removeItem('dte_token')
          localStorage.removeItem('dte_ambiente')
          return false
        }

        // Restaurar el state
        commit('SET_USER', user)
        commit('SET_AMBIENTE', savedAmbiente || '00')  // Default: pruebas
        commit('SET_TOKEN', savedToken)
        return true  // Sesión restaurada exitosamente
      } catch (e) {
        // Si JSON.parse falla, los datos están corruptos
        console.error('Error restaurando sesión:', e)
        // Limpiar datos corruptos
        localStorage.removeItem('dte_user')
        localStorage.removeItem('dte_token')
        localStorage.removeItem('dte_ambiente')
        return false
      }
    }
    return false  // No había sesión guardada
  },

  /**
   * ===========================================================================
   * ACTION: changeAmbiente - Cambiar ambiente de trabajo
   * ===========================================================================
   *
   * Cambia el ambiente de trabajo (Pruebas/Producción).
   *
   * Puede ser necesario cambiar durante la sesión, por ejemplo
   * para hacer pruebas antes de emitir documentos reales.
   *
   * @param {Object} context - Contexto de Vuex
   * @param {string} ambiente - '00' para pruebas, '01' para producción
   */
  changeAmbiente({ commit }, ambiente) {
    commit('SET_AMBIENTE', ambiente)
    localStorage.setItem('dte_ambiente', ambiente)
  },

  // =========================================================================
  // GOOGLE OAUTH 2.0 ACTIONS
  // =========================================================================

  /**
   * ===========================================================================
   * ACTION: googleLogin - Autenticación con Google OAuth 2.0
   * ===========================================================================
   *
   * Autentica un usuario usando Google Sign-In.
   * Si el usuario no existe, se crea automáticamente.
   *
   * @param {Object} context - Contexto de Vuex
   * @param {Object} payload - { idToken, ambiente }
   * @returns {Object} { success: boolean, user?: Object, message?: string }
   */
  async googleLogin({ commit }, { accessToken, ambiente }) {
    try {
      // Llamar al backend con el access token de Google
      const response = await authService.googleLogin(accessToken, ambiente)

      // Transformar respuesta al formato del frontend
      const gSucursalIds = response.sucursalIds || []
      const userData = {
        id: response.userId,
        email: response.email,
        nombre: response.nombreCompleto,
        emisorId: response.emisorId,
        emisorNombre: response.emisorNombre,
        rol: response.rolNombre,
        rolId: response.rolId,
        sucursalIds: gSucursalIds,
        sucursalId: response.sucursalId || (gSucursalIds.length === 1 ? gSucursalIds[0] : null),
        sucursalNombre: response.sucursalNombre || null,
        permisos: response.permisos || [],
        // Flag para forzar cambio de contraseña
        requiereCambioPwd: response.requiereCambioPwd || false
      }

      // Actualizar el state
      commit('SET_USER', userData)
      commit('SET_AMBIENTE', ambiente)
      commit('SET_TOKEN', response.token)

      // Guardar en localStorage
      localStorage.setItem('dte_user', JSON.stringify(userData))
      localStorage.setItem('dte_ambiente', ambiente)
      localStorage.setItem('dte_token', response.token)

      return { success: true, user: userData }
    } catch (error) {
      console.error('Error en googleLogin:', error)
      const message = error.response?.data?.message || 'Error al iniciar sesión con Google'
      return { success: false, message }
    }
  },

  /**
   * ===========================================================================
   * ACTION: changePassword - Cambiar contraseña del usuario
   * ===========================================================================
   *
   * Cambia la contraseña del usuario y limpia el flag de requiereCambioPwd
   *
   * @param {Object} context - Contexto de Vuex
   * @param {Object} payload - { currentPassword, newPassword }
   * @returns {Object} { success: boolean, message?: string }
   */
  async changePassword({ commit }, { currentPassword, newPassword }) {
    try {
      await authService.changePassword(currentPassword, newPassword)
      // Limpiar el flag de requerir cambio de contraseña
      commit('CLEAR_REQUIERE_CAMBIO_PWD')
      return { success: true, message: 'Contraseña actualizada exitosamente' }
    } catch (error) {
      console.error('Error al cambiar contraseña:', error)
      const message = error.response?.data?.message || 'Error al cambiar la contraseña'
      return { success: false, message }
    }
  },

  /**
   * ===========================================================================
   * ACTION: linkGoogleAccount - Vincular cuenta de Google
   * ===========================================================================
   *
   * Vincula una cuenta de Google al usuario autenticado actual.
   *
   * @param {Object} context - Contexto de Vuex
   * @param {string} idToken - ID Token de Google
   * @returns {Object} { success: boolean, message?: string }
   */
  async linkGoogleAccount(_, idToken) {
    try {
      await authService.linkGoogleAccount(idToken)
      return { success: true, message: 'Cuenta de Google vinculada exitosamente' }
    } catch (error) {
      console.error('Error al vincular cuenta de Google:', error)
      const message = error.response?.data?.message || 'No se pudo vincular la cuenta de Google'
      return { success: false, message }
    }
  },

  /**
   * ===========================================================================
   * ACTION: unlinkGoogleAccount - Desvincular cuenta de Google
   * ===========================================================================
   *
   * Desvincula la cuenta de Google del usuario autenticado.
   *
   * @param {Object} context - Contexto de Vuex
   * @returns {Object} { success: boolean, message?: string }
   */
  async unlinkGoogleAccount() {
    try {
      await authService.unlinkGoogleAccount()
      return { success: true, message: 'Cuenta de Google desvinculada exitosamente' }
    } catch (error) {
      console.error('Error al desvincular cuenta de Google:', error)
      const message = error.response?.data?.message || 'No se pudo desvincular la cuenta de Google'
      return { success: false, message }
    }
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL MÓDULO
 * =====================================================================================
 *
 * Exportamos el módulo con la estructura que Vuex espera:
 * - namespaced: Activa el namespace para evitar conflictos
 * - state, getters, mutations, actions: Las partes del módulo
 *
 * USO EN COMPONENTES:
 * -------------------
 * // Composition API
 * const store = useStore()
 * const isAuth = computed(() => store.getters['auth/isAuthenticated'])
 * await store.dispatch('auth/login', credentials)
 *
 * // Options API
 * import { mapGetters, mapActions } from 'vuex'
 * computed: { ...mapGetters('auth', ['isAuthenticated', 'currentUser']) }
 * methods: { ...mapActions('auth', ['login', 'logout']) }
 */
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
